import React, { forwardRef, useEffect, useMemo, useState } from 'react'
import { useBaseInputStyle } from '@linqpal/common-frontend/src/ions/style'
import {
  InputText as AtomicInputText,
  CloseCircle,
  InputContainer,
  InputOuterContainer,
} from '@linqpal/common-frontend/src/ui'
import {
  useFocusedErrorProps,
  useInputValidateInput,
  useInputValidateInputOnBlur,
  withEditableConverter,
} from '@linqpal/common-frontend/src/hooks'
import { composeStyle } from '@linqpal/common-frontend/src/helpers'
import { KeyboardTypeOptions } from 'react-native'

/**
 *
 * @param props
 * @param props.style
 * @param props.inputMask
 * @param props.maskType
 * @param props.maskOptions
 * @param props.nativeProps
 * @param props.error
 * @param { boolean } props.error - This will be the initial Error value overridden by validateInput function.
 * @returns {JSX.Element}
 * @constructor
 */

type PropTypes = {
  style?: any
  inputLength?: number
  disabled?: boolean
  mandatory?: boolean
  secureTextEntry?: boolean
  sibling?: React.ReactNode
  editable?: boolean
  showClearIcon?: boolean
  maskOptions?: any
  nativeProps?: any
  onChangeText?: (a: string) => void
  setInvalid?: (a) => void
  validateInput?: (a) => boolean | string
  onFocus?: () => void
  onBlur?: () => void
  inputMask?: string | boolean
  toRemoveRegex?: string
  error?: string
  value?: string
  siblingProps?: any
  textStyle?: any
  highlight?: boolean
  placeholder?: string
  keyboardType?: KeyboardTypeOptions
}

export const AppInputBase = forwardRef(
  (
    {
      style,
      onFocus: onInputFocus,
      onBlur: onInputBlur,
      error: initialError,
      siblingProps,
      onChangeText,
      toRemoveRegex,
      highlight,
      mandatory,
      ...props
    }: PropTypes,
    ref,
  ) => {
    const { inputBackgroundColor } = useBaseInputStyle()

    const [error, setError] = React.useState(initialError)
    const {
      onBlur,
      onFocus,
      focused,
      error: focusedError,
    } = useFocusedErrorProps(error)
    const { value, validateInput } = props
    const [blur, setBlur] = useState(false)

    const newOnBlur = useInputValidateInputOnBlur({
      validateInput,
      onInputBlur,
      setError,
      toRemoveRegex,
    })
    const newOnChangeText = useInputValidateInput({
      validateInput,
      onChangeText,
      setError,
      toRemoveRegex,
    })
    const valid = useMemo(
      () => !!value && (!validateInput || !validateInput(value)),
      [value, validateInput],
    )

    const finalOnFocus = () => {
      onInputFocus && onInputFocus()
      onFocus()
    }

    const finalOnBlur = (event) => {
      newOnBlur(event.nativeEvent.text)
      onBlur()
      setBlur(true)
    }

    useEffect(() => {
      blur && newOnChangeText && newOnChangeText(value || '')
    }, [blur, valid, newOnChangeText, value])

    useEffect(() => {
      error && setBlur(true)
    }, [error])

    return (
      <InputOuterContainer
        style={[
          {
            borderTopWidth: 0,
            borderLeftWidth: 0,
            borderRightWidth: 0,
            borderBottomWidth: 1,
            borderBottomColor: 'rgba(200, 200, 200, 1)',
            outline: 0,
            borderRadius: 0,
          },
          style,
          highlight ? { borderColor: '#DD4A2C' } : {},
        ]}
        error={focusedError}
      >
        <AtomicInputText
          // eslint-disable-next-line i18next/no-literal-string
          underlineColor="transparent"
          onFocus={finalOnFocus}
          onBlur={finalOnBlur}
          focused={focused}
          inputOuterStyle={composeStyle(inputBackgroundColor, { flex: 1 })}
          theme={{ roundness: 4 }}
          dense
          sibling={
            (props.editable === undefined ||
              props.editable ||
              props.showClearIcon) && (
              <CloseCircle
                value={value}
                disabled={props.disabled}
                onChangeText={onChangeText!}
                {...1}
              />
            )
          }
          InputContainer={InputContainer}
          error={focusedError}
          paddingBottom={13}
          {...props}
          ref={ref}
          onChangeText={newOnChangeText}
          style={{ textAlign: 'center', ...props.textStyle }}
        />
      </InputOuterContainer>
    )
  },
)

export const AppInput = withEditableConverter(AppInputBase)
